/** The blocks a dashboard can hold. */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, FileText } from 'lucide-react';
import { addDays, isOpen, type DashboardBlock, type TasksScope } from '@nexus/core';
import { parseMarkdown } from '@nexus/markdown';
import { mutatePageDoc, type Task } from '@nexus/workspace';
import { usePages, useStore } from '../../db/context';
import { useDatabase } from '../../db/database';
import { useProjects, useTasks } from '../../db/tasks';
import { ensureDailyNote } from '../../lib/pages';
import { useUi } from '../../state/ui';
import { DatabaseView } from '../database/DatabaseView';
import { ProgressBar } from '../tasks/ProjectsView';
import { TaskCalendar } from '../tasks/TaskCalendar';
import { TaskRow } from '../tasks/TaskRow';
import { byDueThenPriority, todayIso } from '../tasks/format';
import { PageIcon } from '../PageIcon';

const MAX_TASKS = 8;

/** Which tasks a tasks block shows (same rules as the task views). */
function tasksFor(tasks: Task[], scope: TasksScope, today = todayIso()): Task[] {
  const open = tasks.filter((x) => isOpen(x.status));
  const week = addDays(today, 6);
  const list =
    scope === 'overdue'
      ? open.filter((x) => x.due && x.due < today)
      : scope === 'week'
        ? open.filter((x) => x.due && x.due <= week)
        : scope === 'inbox'
          ? open.filter((x) => !x.due && !x.scheduled && !x.projectId)
          : open.filter((x) => (x.due && x.due <= today) || (x.scheduled && x.scheduled <= today));
  return list.sort(byDueThenPriority);
}

export function TasksBlock({ block }: { block: DashboardBlock }) {
  const { t } = useTranslation();
  const tasks = useTasks();
  const open = useUi((s) => s.open);
  const scope = block.scope ?? 'today';
  if (!tasks) return null;
  const list = tasksFor(tasks, scope);
  return (
    <div className="flex flex-col">
      {list.length === 0 ? (
        <p className="py-3 text-sm text-fg-muted">{t(`dashboard.noTasks.${scope}`)}</p>
      ) : (
        list
          .slice(0, MAX_TASKS)
          .map((x) => <TaskRow key={x.id} task={x} projectTitle={null} compact />)
      )}
      <button
        type="button"
        onClick={() => open({ kind: 'tasks', tab: scope })}
        className="mt-1 flex items-center gap-1 self-start rounded px-1 text-xs text-fg-muted hover:bg-bg-hover hover:text-fg"
      >
        {list.length > MAX_TASKS
          ? t('dashboard.moreTasks', { count: list.length - MAX_TASKS })
          : t('dashboard.allTasks')}
        <ArrowRight className="size-3" />
      </button>
    </div>
  );
}

export function DatabaseBlock({ block }: { block: DashboardBlock }) {
  const { t } = useTranslation();
  const db = useDatabase(block.databaseId ?? '');
  const pages = usePages();
  const title = pages?.byId.get(block.databaseId ?? '')?.title ?? '';
  if (!block.databaseId)
    return <p className="text-sm text-fg-muted">{t('dashboard.chooseDatabase')}</p>;
  if (db === undefined) return null;
  if (db === null) return <p className="text-sm text-fg-muted">{t('dashboard.databaseGone')}</p>;
  return (
    <DatabaseView
      pageId={block.databaseId}
      db={db}
      embedded={{ viewId: block.viewId, title: title || t('page.untitled') }}
    />
  );
}

export function RecentBlock({ block }: { block: DashboardBlock }) {
  const { t, i18n } = useTranslation();
  const pages = usePages();
  const open = useUi((s) => s.open);
  // Relative times stay current: the clock ticks once a minute.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, []);
  const fmt = new Intl.RelativeTimeFormat(i18n.language, { numeric: 'auto' });
  const ago = (ms: number) => {
    const minutes = Math.round((ms - now) / 60_000);
    if (minutes > -60) return fmt.format(minutes, 'minute');
    const hours = Math.round(minutes / 60);
    if (hours > -24) return fmt.format(hours, 'hour');
    return fmt.format(Math.round(hours / 24), 'day');
  };
  const recent = [...(pages?.list ?? [])]
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, block.limit ?? 8);
  if (!recent.length) return <p className="text-sm text-fg-muted">{t('sidebar.empty')}</p>;
  return (
    <ul className="flex flex-col">
      {recent.map((p) => (
        <li key={p.id}>
          <button
            type="button"
            onClick={() => open({ kind: 'page', pageId: p.id })}
            className="flex w-full items-center gap-2 rounded px-1 py-1 text-left text-sm hover:bg-bg-hover"
          >
            <span className="w-4 shrink-0 text-center">
              <PageIcon icon={p.icon} fallback={<FileText className="size-3.5 text-fg-muted" />} />
            </span>
            <span className="min-w-0 flex-1 truncate">{p.title || t('page.untitled')}</span>
            <span className="shrink-0 text-xs text-fg-muted">{ago(p.updatedAt)}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

export function CalendarBlock() {
  const tasks = useTasks();
  return tasks ? <TaskCalendar tasks={tasks} place="dashboard" /> : null;
}

export function ProjectsBlock() {
  const { t } = useTranslation();
  const data = useProjects();
  const open = useUi((s) => s.open);
  if (!data) return null;
  const projects = data.projects.filter((p) => p.open > 0 || p.done === 0).slice(0, 12);
  if (!projects.length) return <p className="text-sm text-fg-muted">{t('dashboard.noProjects')}</p>;
  return (
    <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
      {projects.map((p) => (
        <li key={p.page.id}>
          <button
            type="button"
            onClick={() => open({ kind: 'page', pageId: p.page.id })}
            className="flex w-full flex-col gap-1 rounded-lg border border-border px-2 py-1.5 text-left hover:bg-bg-hover"
          >
            <span className="truncate text-sm font-medium">
              {p.page.icon && (
                <>
                  <PageIcon icon={p.page.icon} />{' '}
                </>
              )}
              {p.page.title || t('page.untitled')}
            </span>
            <ProgressBar
              value={p.progress}
              label={t('projects.progress', {
                done: p.done,
                total: p.done + p.open,
                pct: Math.round(p.progress * 100),
              })}
            />
            <span className="text-xs text-fg-muted">
              {t('projects.progress', {
                done: p.done,
                total: p.done + p.open,
                pct: Math.round(p.progress * 100),
              })}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

/** A quick note, added to today's daily note (Markdown: "- [ ] …" becomes a task). */
export function NoteBlock() {
  const { t, i18n } = useTranslation();
  const store = useStore();
  const [text, setText] = useState('');
  const [saved, setSaved] = useState(false);
  const add = async () => {
    const value = text.trim();
    if (!value) return;
    setText('');
    const page = await ensureDailyNote(store, i18n.language);
    const extra = parseMarkdown(value).doc.content ?? [];
    await mutatePageDoc(store, page.id, (doc) => ({
      ...doc,
      content: [...(doc.content ?? []), ...extra],
    }));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };
  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        void add();
      }}
    >
      <textarea
        rows={4}
        value={text}
        aria-label={t('dashboard.noteLabel')}
        placeholder={t('dashboard.notePlaceholder')}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            void add();
          }
        }}
        className="w-full resize-y rounded-lg border border-border bg-bg px-2 py-1.5 text-sm outline-none focus:border-accent"
      />
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-fg-muted" aria-live="polite">
          {saved ? t('dashboard.noteSaved') : t('dashboard.noteHint')}
        </span>
        <button
          type="submit"
          disabled={!text.trim()}
          className="rounded-full bg-accent px-2 py-1 text-xs font-medium text-accent-fg disabled:opacity-50"
        >
          {t('dashboard.noteAdd')}
        </button>
      </div>
    </form>
  );
}
